import React, { useEffect, useRef } from "react";
import Plotly from "plotly.js-dist-min";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useNavigate } from "react-router-dom";

interface Marker {
    name: string;
    latLng: [number, number];
    id: number;
}

interface USLocationMapProps {
    markers: Marker[];
    onHover?: (location: string | null) => void;
}

const USLocationMap: React.FC<USLocationMapProps> = ({ markers, onHover }) => {
    const navigate = useNavigate();
    const plotRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!plotRef.current || markers.length === 0) return;

        const lats = markers.map(marker => marker.latLng[0]);
        const lons = markers.map(marker => marker.latLng[1]);
        const texts = markers.map(marker => marker.name);

        const trace: Partial<Plotly.PlotData> = {
            type: "scattergeo",
            mode: "markers",
            locationmode: "USA-states",
            lat: lats,
            lon: lons,
            text: texts,
            marker: {
                size: 8,
                color: "red",
            },
            hoverinfo: "text",
        };

        const layout: Partial<Plotly.Layout> = {
            title: "Sensor Locations in the US",
            geo: {
                scope: "usa",
                projection: { type: "albers usa" },
                showland: true,
                landcolor: "rgb(217, 217, 217)",
                subunitwidth: 1,
                countrywidth: 1,
                subunitcolor: "rgb(255,255,255)",
                countrycolor: "rgb(255,255,255)",
            },
            autosize: true,
            margin: { l: 0, r: 0, b: 0, t: 0, pad: 0 },
        };

        const config: Partial<Plotly.Config> = {
            responsive: true,
            displayModeBar: false,
        };

        Plotly.newPlot(plotRef.current, [trace], layout, config).then((plotInstance) => {
            plotInstance.on("plotly_hover", (eventData) => {
                if (eventData.points.length > 0 && onHover) {
                    onHover(eventData.points[0].text);
                }
            });

            plotInstance.on("plotly_unhover", () => {
                if (onHover) onHover(null);
            });
            // Marker click event
            plotInstance.on("plotly_click", (clickData) => {
                // If you only want to navigate when a marker is clicked:
                // (the user might also click background)
                if (clickData.points.length > 0) {
                    const pointIndex = clickData.points[0].pointNumber;
                    const markerId = markers[pointIndex].id; // read the .id from your marker data
                    //navigate(`/ExampleLayout/${markerId}`);
                    navigate(`/locations/${markerId}`);
                }
            });
        });


        // Handle window resize
        const handleResize = () => {
            if (plotRef.current) {
                Plotly.Plots.resize(plotRef.current);
            }
        };

        window.addEventListener("resize", handleResize);

        // Cleanup function
        return () => {
            window.removeEventListener("resize", handleResize);
            if (plotRef.current) {
                Plotly.purge(plotRef.current);
            }
        };
    }, [markers, onHover]);

    return (
        <Card sx={{ height: "100%" }}>
            <MDBox p={2}>
                <MDTypography variant="h6">Sensor Locations</MDTypography>
            </MDBox>
            <MDBox
                p={0}
                sx={{
                    height: "calc(100% - 0px)",
                    minHeight: "300px",
                }}
            >
                <div
                    ref={plotRef}
                    style={{
                        width: "100%",
                        height: "100%",
                    }}
                />
            </MDBox>
        </Card>
    );
};

export default USLocationMap;
